// Customer-facing entertainment modules: 4 mini games + 3 AI placeholders.
// Designed as small, self-contained components — no external state, no network
// calls (except the AI hooks the host can wire up later). Each game has:
//   - a back button (← Games)
//   - score/move counter where applicable
//   - a "Play Again" button shown after completion
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Gamepad2, Heart, BookOpen, Smile, ArrowLeft, Sparkles,
  Grid3X3
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Memory Match — 4×4 grid (8 pairs) of emojis. Tap to flip; matched pairs stay
// face-up. Tracks moves, shows "Play Again" when all pairs are found.
// ---------------------------------------------------------------------------
function MemoryMatch({ onBack }) {
  const POOL = ['🍎','🍌','🍇','🍓','🍕','🌮','🍩','🍦','🚗','🚀','⚽','🎈','🐶','🐱','🦄','🐸'];
  const buildDeck = () => {
    const picked = [...POOL].sort(() => Math.random() - 0.5).slice(0, 8);
    const cards = [...picked, ...picked]
      .map((emoji, i) => ({ id: i, emoji, flipped: false, matched: false }))
      .sort(() => Math.random() - 0.5);
    return cards;
  };
  const [cards, setCards] = useState(buildDeck);
  const [first, setFirst] = useState(null);
  const [second, setSecond] = useState(null);
  const [moves, setMoves] = useState(0);
  const [busy, setBusy] = useState(false);
  const allMatched = cards.every(c => c.matched);

  const onCardTap = (idx) => {
    if (busy) return;
    if (cards[idx].flipped || cards[idx].matched) return;
    const next = [...cards];
    next[idx] = { ...next[idx], flipped: true };
    setCards(next);
    if (first === null) { setFirst(idx); return; }
    setSecond(idx);
    setMoves(m => m + 1);
    setBusy(true);
    setTimeout(() => {
      setCards(prev => {
        const u = [...prev];
        if (u[first].emoji === u[idx].emoji) {
          u[first] = { ...u[first], matched: true };
          u[idx] = { ...u[idx], matched: true };
        } else {
          u[first] = { ...u[first], flipped: false };
          u[idx] = { ...u[idx], flipped: false };
        }
        return u;
      });
      setFirst(null); setSecond(null); setBusy(false);
    }, 700);
  };
  const reset = () => {
    setCards(buildDeck()); setFirst(null); setSecond(null); setMoves(0); setBusy(false);
  };
  return (
    <div className="space-y-3">
      <GameHeader onBack={onBack} title="Memory Match" />
      <div className="flex items-center justify-between text-sm font-semibold text-stone-700">
        <span>Moves: <span className="text-orange-600 tabular-nums">{moves}</span></span>
        <span>Pairs: {cards.filter(c => c.matched).length / 2} / 8</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {cards.map((c, i) => {
          const showFace = c.flipped || c.matched;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onCardTap(i)}
              data-testid={`memory-card-${i}`}
              className={`aspect-square rounded-xl border-2 flex items-center justify-center text-3xl transition-all ${
                c.matched ? 'border-emerald-400 bg-emerald-50' : showFace ? 'border-orange-300 bg-white' : 'border-stone-200 bg-stone-100 hover:border-stone-300'
              }`}
            >
              {showFace ? c.emoji : <Grid3X3 size={20} className="text-stone-400" />}
            </button>
          );
        })}
      </div>
      {allMatched && (
        <div className="mt-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
          <p className="font-bold text-emerald-800">🎉 You matched all pairs in {moves} moves!</p>
          <Button onClick={reset} className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="memory-play-again">Play Again</Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quiz — 5 fun trivia questions, multiple choice, score at the end.
// ---------------------------------------------------------------------------
const QUIZ_BANK = [
  { q: 'Which planet is known as the Red Planet?', opts: ['Earth', 'Mars', 'Jupiter', 'Venus'], a: 1 },
  { q: 'How many legs does a spider have?', opts: ['6', '8', '10', '4'], a: 1 },
  { q: 'What do bees produce?', opts: ['Milk', 'Honey', 'Silk', 'Wax'], a: 1 },
  { q: 'Which is the largest ocean?', opts: ['Atlantic', 'Indian', 'Pacific', 'Arctic'], a: 2 },
  { q: 'How many continents are there?', opts: ['5', '6', '7', '8'], a: 2 },
  { q: 'What is the tallest animal?', opts: ['Elephant', 'Giraffe', 'Horse', 'Zebra'], a: 1 },
  { q: 'Which fruit is yellow and curved?', opts: ['Apple', 'Mango', 'Banana', 'Orange'], a: 2 },
  { q: 'How many days in a year?', opts: ['360', '365', '370', '375'], a: 1 },
];
function Quiz({ onBack }) {
  const [questions] = useState(() => [...QUIZ_BANK].sort(() => Math.random() - 0.5).slice(0, 5));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const done = idx >= questions.length;
  const onPick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === questions[idx].a) setScore(s => s + 1);
    setTimeout(() => { setIdx(n => n + 1); setPicked(null); }, 800);
  };
  const reset = () => { setIdx(0); setScore(0); setPicked(null); };
  if (done) {
    return (
      <div className="space-y-3">
        <GameHeader onBack={onBack} title="Quiz" />
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
          <p className="font-bold text-emerald-800 text-lg">You got {score} / {questions.length}!</p>
          <Button onClick={reset} className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quiz-play-again">Play Again</Button>
        </div>
      </div>
    );
  }
  const q = questions[idx];
  return (
    <div className="space-y-3">
      <GameHeader onBack={onBack} title="Quiz" />
      <div className="flex items-center justify-between text-sm font-semibold text-stone-700">
        <span>Q{idx + 1} / {questions.length}</span>
        <span>Score: <span className="text-orange-600">{score}</span></span>
      </div>
      <div className="p-4 rounded-xl bg-white border border-stone-200">
        <p className="font-bold text-stone-900 text-base">{q.q}</p>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {q.opts.map((opt, i) => {
          const isPickedRight = picked === i && i === q.a;
          const isPickedWrong = picked === i && i !== q.a;
          return (
            <button
              key={i}
              type="button"
              data-testid={`quiz-option-${i}`}
              onClick={() => onPick(i)}
              className={`w-full text-left p-3 rounded-xl border-2 font-medium transition-all ${
                isPickedRight ? 'border-emerald-400 bg-emerald-50 text-emerald-900' :
                isPickedWrong ? 'border-red-400 bg-red-50 text-red-900' :
                'border-stone-200 bg-white hover:border-orange-300'
              }`}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Math Game — 8 simple +/− problems with 4 choices each. Tracks score.
// ---------------------------------------------------------------------------
function MathGame({ onBack }) {
  const buildRound = () => {
    const list = [];
    for (let i = 0; i < 8; i++) {
      const a = 1 + Math.floor(Math.random() * 19);
      const b = 1 + Math.floor(Math.random() * 19);
      const op = Math.random() < 0.5 ? '+' : '-';
      const correct = op === '+' ? a + b : Math.abs(a - b);
      const big = Math.max(a, b), small = Math.min(a, b);
      const expr = op === '+' ? `${a} + ${b}` : `${big} − ${small}`;
      const opts = new Set([correct]);
      while (opts.size < 4) opts.add(Math.max(0, correct + Math.floor(Math.random() * 11) - 5));
      list.push({ expr, correct, opts: [...opts].sort(() => Math.random() - 0.5) });
    }
    return list;
  };
  const [problems, setProblems] = useState(buildRound);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const done = idx >= problems.length;
  const onPick = (n) => {
    if (picked !== null) return;
    setPicked(n);
    if (n === problems[idx].correct) setScore(s => s + 1);
    setTimeout(() => { setIdx(i => i + 1); setPicked(null); }, 700);
  };
  const reset = () => { setProblems(buildRound()); setIdx(0); setScore(0); setPicked(null); };
  if (done) {
    return (
      <div className="space-y-3">
        <GameHeader onBack={onBack} title="Math Game" />
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
          <p className="font-bold text-emerald-800 text-lg">Score: {score} / {problems.length}</p>
          <Button onClick={reset} className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="math-play-again">Play Again</Button>
        </div>
      </div>
    );
  }
  const p = problems[idx];
  return (
    <div className="space-y-3">
      <GameHeader onBack={onBack} title="Math Game" />
      <div className="flex items-center justify-between text-sm font-semibold text-stone-700">
        <span>Q{idx + 1} / {problems.length}</span>
        <span>Score: <span className="text-orange-600">{score}</span></span>
      </div>
      <div className="p-6 rounded-xl bg-white border border-stone-200 text-center">
        <p className="font-extrabold text-stone-900 text-3xl tabular-nums">{p.expr} = ?</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {p.opts.map((n, i) => {
          const isRight = picked === n && n === p.correct;
          const isWrong = picked === n && n !== p.correct;
          return (
            <button
              key={i}
              type="button"
              data-testid={`math-option-${i}`}
              onClick={() => onPick(n)}
              className={`p-4 rounded-xl border-2 font-bold text-xl transition-all ${
                isRight ? 'border-emerald-400 bg-emerald-50 text-emerald-900' :
                isWrong ? 'border-red-400 bg-red-50 text-red-900' :
                'border-stone-200 bg-white hover:border-orange-300'
              }`}
            >{n}</button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spelling Game — show a word with one letter blanked. Kid types the letter.
// ---------------------------------------------------------------------------
const SPELL_WORDS = ['APPLE','BANANA','CARROT','DOG','ELEPHANT','FROG','GRAPE','HOUSE','IGLOO','JUICE','KITE','LION','MANGO','NEST','OCEAN','PIZZA','QUEEN','RABBIT','SUN','TIGER'];
function SpellingGame({ onBack }) {
  const buildRound = () => {
    return [...SPELL_WORDS].sort(() => Math.random() - 0.5).slice(0, 6).map(w => {
      const idx = Math.floor(Math.random() * w.length);
      return { word: w, hideIdx: idx, missing: w[idx] };
    });
  };
  const [words, setWords] = useState(buildRound);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [val, setVal] = useState('');
  const [feedback, setFeedback] = useState(null); // 'right'|'wrong'|null
  const done = idx >= words.length;
  const submit = (e) => {
    e?.preventDefault?.();
    if (feedback || !val) return;
    const ok = val.trim().toUpperCase() === words[idx].missing;
    if (ok) setScore(s => s + 1);
    setFeedback(ok ? 'right' : 'wrong');
    setTimeout(() => { setIdx(i => i + 1); setVal(''); setFeedback(null); }, 900);
  };
  const reset = () => { setWords(buildRound()); setIdx(0); setScore(0); setVal(''); setFeedback(null); };
  if (done) {
    return (
      <div className="space-y-3">
        <GameHeader onBack={onBack} title="Spelling" />
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
          <p className="font-bold text-emerald-800 text-lg">Score: {score} / {words.length}</p>
          <Button onClick={reset} className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="spell-play-again">Play Again</Button>
        </div>
      </div>
    );
  }
  const w = words[idx];
  const display = w.word.split('').map((ch, i) => i === w.hideIdx ? '_' : ch).join(' ');
  return (
    <div className="space-y-3">
      <GameHeader onBack={onBack} title="Spelling" />
      <div className="flex items-center justify-between text-sm font-semibold text-stone-700">
        <span>Q{idx + 1} / {words.length}</span>
        <span>Score: <span className="text-orange-600">{score}</span></span>
      </div>
      <div className="p-6 rounded-xl bg-white border border-stone-200 text-center">
        <p className="font-extrabold text-stone-900 text-3xl tracking-widest">{display}</p>
        <p className="text-sm text-stone-500 mt-2">Fill in the missing letter</p>
      </div>
      <form onSubmit={submit} className="flex gap-2">
        <Input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value.slice(0, 1))}
          maxLength={1}
          className={`text-center text-2xl font-bold uppercase ${feedback === 'right' ? 'border-emerald-400 bg-emerald-50' : feedback === 'wrong' ? 'border-red-400 bg-red-50' : ''}`}
          data-testid="spell-input"
          placeholder="?"
        />
        <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="spell-submit">Check</Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Game selector — small grid of 4 game tiles.
// ---------------------------------------------------------------------------
function GameSelector({ onPick }) {
  // 4 colorful, kid-friendly game posters served from /public/games. Each
  // image is paired carefully with the game it launches — do NOT swap.
  // Two formats per tile: WebP (~35-45 KB, ~50× smaller than the original
  // 1.9 MB PNG) for modern browsers, with a PNG fallback for old WebViews.
  const tiles = [
    { id: 'memory', label: 'Memory Match', base: '/games/memory' },
    { id: 'quiz',   label: 'Quiz',         base: '/games/quiz' },
    { id: 'math',   label: 'Math',         base: '/games/math' },
    { id: 'spell',  label: 'Spelling',     base: '/games/spell' },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {tiles.map(t => (
        <button
          key={t.id}
          type="button"
          data-testid={`game-tile-${t.id}`}
          onClick={() => onPick(t.id)}
          aria-label={`Play ${t.label}`}
          className="aspect-square rounded-2xl overflow-hidden shadow-md active:scale-95 transition-transform bg-stone-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
        >
          <picture>
            <source srcSet={`${t.base}.webp`} type="image/webp" />
            <img
              src={`${t.base}.png`}
              alt={t.label}
              loading="eager"
              decoding="async"
              fetchpriority="high"
              width="512"
              height="512"
              className="w-full h-full object-cover select-none pointer-events-none"
              draggable={false}
            />
          </picture>
        </button>
      ))}
    </div>
  );
}

function GameHeader({ onBack, title }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        data-testid="game-back-btn"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-stone-600 hover:bg-stone-100"
      >
        <ArrowLeft size={16} /> Games
      </button>
      <span className="font-bold text-stone-900">{title}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mini Games entry — picker + selected game wrapper.
// ---------------------------------------------------------------------------
export function MiniGamesSheet({ open, onOpenChange }) {
  const [selected, setSelected] = useState(null);
  // Reset to selector each time the sheet re-opens (fresh slate).
  useEffect(() => { if (open) setSelected(null); }, [open]);
  const Game = useMemo(() => {
    switch (selected) {
      case 'memory': return MemoryMatch;
      case 'quiz': return Quiz;
      case 'math': return MathGame;
      case 'spell': return SpellingGame;
      default: return null;
    }
  }, [selected]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[88vh] rounded-t-3xl p-4 overflow-y-auto" data-testid="mini-games-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Gamepad2 size={20} className="text-orange-600" /> Mini Games
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          {Game ? <Game onBack={() => setSelected(null)} /> : <GameSelector onPick={setSelected} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// AI placeholder sheets for Quotes / Poems / Jokes — host-keywords-driven.
// Calls `POST /api/customer/{rid}/entertainment` and renders the response.
// ---------------------------------------------------------------------------
export function AiPlaceholderSheet({ open, onOpenChange, kind = 'quotes', restaurantId }) {
  const meta = {
    quotes: { title: 'Love Quotes', icon: Heart, hint: 'Words about people, places, and feelings (max 25 words)' },
    poems:  { title: 'Poems', icon: BookOpen, hint: 'A theme, a mood, a moment (max 35 words)' },
    jokes:  { title: 'Jokes', icon: Smile, hint: 'A topic to tickle the funny bone (max 35 words)' },
  }[kind] || { title: 'Coming soon', icon: Sparkles, hint: 'AI content' };
  const Icon = meta.icon;
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  // Reset state every time the sheet (re)opens.
  useEffect(() => { if (open) { setKeywords(''); setContent(''); setError(''); setLoading(false); } }, [open, kind]);
  const generate = async () => {
    setLoading(true); setError(''); setContent('');
    try {
      const list = keywords.split(/[,\s]+/).map(s => s.trim()).filter(Boolean).slice(0, 4);
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiBase}/api/customer/${restaurantId}/entertainment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, keywords: list }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setContent(data.content || '');
    } catch (e) {
      setError(e.message || 'Could not generate content');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[88vh] rounded-t-3xl p-4 overflow-y-auto" data-testid={`ai-sheet-${kind}`}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Icon size={20} className="text-orange-600" /> {meta.title}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <div className="p-4 rounded-2xl border border-dashed border-orange-300 bg-orange-50/50 text-center">
            <Sparkles size={28} className="mx-auto text-orange-500" />
            <p className="mt-2 text-sm font-semibold text-stone-800">Pick 3-4 keywords</p>
            <p className="text-xs text-stone-500">{meta.hint}</p>
          </div>
          <Input
            data-testid={`ai-${kind}-input`}
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
            placeholder="e.g. ocean, sunset, hope"
            className="text-center"
            disabled={loading}
            onKeyDown={e => { if (e.key === 'Enter' && !loading) generate(); }}
          />
          <Button
            type="button"
            disabled={loading}
            onClick={generate}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            data-testid={`ai-${kind}-generate`}
          >
            <Sparkles size={16} className="mr-1" /> {loading ? 'Generating…' : (content ? 'Generate again' : 'Generate')}
          </Button>
          {error && (
            <p className="text-center text-sm text-red-600" data-testid={`ai-${kind}-error`}>{error}</p>
          )}
          {content && (
            <div
              data-testid={`ai-${kind}-result`}
              className="p-5 rounded-2xl border border-stone-200 bg-white shadow-sm whitespace-pre-line text-center text-stone-800 text-base leading-relaxed"
              style={{ fontFamily: 'Outfit' }}
            >
              {content}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Customer entertainment buttons — 4 squares under the chat icon.
// Conditionally renders only the buttons whose effective flag is enabled.
// ---------------------------------------------------------------------------
export function EntertainmentButtons({ enabled, onOpen, tr }) {
  // Static i18n: when the customer passes a `tr` translator we look up the
  // localized label; otherwise we fall back to the English source (used by
  // older callers that don't import the i18n helper yet).
  const lbl = (key, fallback) => (typeof tr === 'function' ? tr(key) : fallback);
  const tiles = [
    { id: 'games',  label: lbl('ent_games',  'Games'),  icon: Gamepad2,  visible: !!enabled?.games  },
    { id: 'quotes', label: lbl('ent_quotes', 'Quotes'), icon: Heart,     visible: !!enabled?.quotes },
    { id: 'poems',  label: lbl('ent_poems',  'Poems'),  icon: BookOpen,  visible: !!enabled?.poems  },
    { id: 'jokes',  label: lbl('ent_jokes',  'Jokes'),  icon: Smile,     visible: !!enabled?.jokes  },
  ].filter(t => t.visible);
  if (tiles.length === 0) return null;
  return (
    <div data-testid="customer-entertainment-row" className="mt-4 grid grid-cols-4 gap-2 w-full max-w-xs mx-auto">
      {tiles.map(t => {
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            data-testid={`entertainment-btn-${t.id}`}
            onClick={() => onOpen(t.id)}
            className="aspect-square rounded-2xl bg-[#0F4A6B] hover:bg-[#0d4262] text-white flex flex-col items-center justify-center gap-1 shadow-md active:scale-95 transition-transform"
          >
            <Icon size={22} />
            <span className="text-[10px] font-semibold uppercase tracking-wide">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}
